import { bytesToHex, hexToBytes } from "@sundae/cardano-utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { startTransition, useCallback } from "react";
import { signDataWithConnectedWallet } from "../modules/signData";
import { Actions, useWalletContext } from "../store";
import { displayError } from "../utils/displayError";
import { useHeartbeatLogger } from "./useHeartbeatLogger";
import { useWalletApi } from "./useWalletApi";
import { useWalletError } from "./useWalletError";
const HOOK_CONTEXT = "useWallet";
export const useWallet = () => {
    const queryClient = useQueryClient();
    const { state, dispatch } = useWalletContext();
    const { api, connectApi, clearApi } = useWalletApi();
    const hb = useHeartbeatLogger();
    const { clearError } = useWalletError();
    /**
     * Logs out the user's wallet and clears the cache.
     */
    const logoutWallet = useCallback(async () => {
        await queryClient.cancelQueries();
        clearApi();
        await Actions.logout(dispatch);
    }, [queryClient]);
    /**
     * Main mutation to connect a new wallet. This is just
     * adding a new API reference, which will trigger all
     * other data to update.
     */
    const { mutateAsync: connectWallet, isLoading: isConnectingWallet } = useMutation(["cardano.connectWallet"], async (key) => {
        hb.logStartingConnection(HOOK_CONTEXT);
        const newApi = await connectApi({ overrideWallet: key });
        return Boolean(newApi) ? key : false;
    }, {
        onMutate: (key) => {
            startTransition(() => {
                Actions.setActiveWallet(key, dispatch);
            });
        },
        onSuccess: (key) => {
            if (!key) {
                hb.logDoneWithoutChanges(HOOK_CONTEXT);
                return;
            }
            hb.logDoneWithChanges(HOOK_CONTEXT);
        },
        onError: async (e) => {
            Actions.setError(await displayError(e), dispatch);
        },
    });
    /**
     * Main mutation to delegate to a pool. It requires the pool's keyhash,
     * and optionally receives the transaction's TTL, and whether or not
     * the delegation is a registration or an update.
     */
    const { mutateAsync: delegateToPool, isLoading: isDelegatingToPool } = useMutation(["cardano.getAssets", state.activeWallet, state.details, api], async ({ poolKeyHash, ttl, isRegistration = false }) => {
        const fee = isRegistration ? 175000 : 173000;
        const utxos = await api.getUtxos();
        let outputUtxo;
        for (const utxo of utxos) {
            if (Number(utxo.amount.coin) > fee && !utxo.amount?.multiasset) {
                outputUtxo = {
                    id: hexToBytes(utxo.transaction_id),
                    index: Number(utxo.index),
                    amount: Number(utxo.amount.coin),
                };
                break;
            }
        }
        if (!outputUtxo) {
            const collateral = await api.getCollateral();
            if (collateral?.[0]) {
                throw new Error("No UTXO with enough ADA was found! You have collateral set, so try adding some more ADA to your wallet.");
            }
            else {
                throw new Error("No UTXO with enough ADA was found!");
            }
        }
        const body = new Map();
        body.set(0, [[outputUtxo.id, outputUtxo.index]]);
        body.set(1, [
            [
                hexToBytes(state.details?.hex?.mainAddress),
                outputUtxo.amount - (isRegistration ? fee + 2000000 : fee),
            ],
        ]);
        body.set(2, fee);
        ttl && body.set(3, ttl);
        const delegation = [];
        if (isRegistration) {
            delegation.push([
                0,
                [0, hexToBytes(state.details?.credentials?.staking)],
            ]);
        }
        delegation.push([
            2,
            [
                0,
                hexToBytes(state.details?.credentials?.staking), // staking key hash
            ],
            hexToBytes(poolKeyHash),
        ]);
        body.set(4, delegation);
        // Get witnesses.
        const cbor = await import("cbor-web");
        const tx = bytesToHex(cbor.encode([body, {}, true, null]));
        const witnesses = cbor.decode(await api.signTx(tx, false));
        const txBody = cbor.encode([body, witnesses, true, null]);
        const id = await api.submitTx(bytesToHex(txBody));
        return id;
    }, {
        onError: async (error) => {
            Actions.setError(await displayError(error), dispatch);
        },
        cacheTime: 30000, // 30 seconds
    });
    /**
     * Mutation to sign a data payload and return a response that works with
     * the {@link verifyData} function in `/server`.
     */
    const { mutateAsync: signData, isLoading: isSigningData } = useMutation([api], async ({ payload, signingAddress }) => {
        clearError();
        if (!api) {
            Actions.setError("There is no wallet connected!", dispatch);
            return;
        }
        return await signDataWithConnectedWallet({
            signingAddress,
            payload,
            connectedWallet: api,
        });
    });
    return {
        state,
        connectWallet,
        isConnectingWallet,
        delegateToPool,
        isDelegatingToPool,
        signData,
        isSigningData,
        logoutWallet,
    };
};
//# sourceMappingURL=useWallet.js.map